import './price-range.css';
import 'rc-slider/assets/index.css';

import React, { useMemo, useState } from 'react';
import { IRange } from '../../models/range.model';
import { SearchFilterContainer } from './search-filter-container';
import { Range } from 'rc-slider';
import { PRICE_RANGE_MIN, PRICE_RANGE_MAX, PRICE_RANGE_STEP, PRICE_RANGE_ULTD, LEASE_PRICE_RANGE_MIN, LEASE_PRICE_RANGE_ULTD, LEASE_PRICE_RANGE_STEP, LEASE_PRICE_RANGE_MAX } from '../../util/listing-search.constants';
import { convertToInternationalCurrencySystem, formatNumber } from '../../util/format-number';
import { rangeHighToString } from '../../util/range-high-to-string';
import { iconDollar } from '../../util/icons';

export interface PriceRangeProps {
  isOpen: boolean;
  listPriceRange: IRange;
  leasePriceRange: IRange;
  isFloating?: boolean;
  showListPrice?: boolean;
  showLeasePrice?: boolean;
  setSearchOption(key: string, value: string | number): void;
  open(): void;
  close(): void;
}

export const PriceRange: React.FC<PriceRangeProps> = props => {
  const [listPriceRange, setListPriceRange] = useState<IRange>(props.listPriceRange);
  const [leasePriceRange, setLeasePriceRange] = useState<IRange>(props.leasePriceRange);

  const onAfterChangeList = (value: number[]) => {
    props.setSearchOption('listPriceRangeLow', value[0]);
    props.setSearchOption('listPriceRangeHigh', listPriceRange.high === PRICE_RANGE_ULTD ? 0 : value[1]);
  };

  const onChangeList = (value: number[]) => {
    setListPriceRange({ low: value[0], high: value[1] });
  };

  const onAfterChangeLease = (value: number[]) => {
    props.setSearchOption('leasePriceRangeLow', value[0]);
    props.setSearchOption('leasePriceRangeHigh', leasePriceRange.high === LEASE_PRICE_RANGE_ULTD ? 0 : value[1]);
  };

  const onChangeLease = (value: number[]) => {
    setLeasePriceRange({ low: value[0], high: value[1] });
  };

  const listPriceControl = useMemo(() => {
    return props.showListPrice
      ?
      <div className="form-field">
        <div className="form-field-label"><span className="icon">{iconDollar}</span>List Price:
          ${formatNumber(listPriceRange.low)}
          {' - '}
          {rangeHighToString(listPriceRange.high, PRICE_RANGE_MAX, false, formatNumber(listPriceRange.high))}
        </div>
        <div className="range-slider">
          <Range
            min={PRICE_RANGE_MIN}
            max={PRICE_RANGE_ULTD}
            step={PRICE_RANGE_STEP}
            defaultValue={[listPriceRange.low, listPriceRange.high]}
            onChange={onChangeList}
            onAfterChange={onAfterChangeList}
            allowCross={false}
            pushable={PRICE_RANGE_STEP}
          />
        </div>
      </div>
      : <div />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showListPrice, listPriceRange.high, listPriceRange.low]);

  const leasePriceControl = useMemo(() => {
    return props.showLeasePrice
      ? <div className="form-field">
        <div className="form-field-label"><span className="icon">{iconDollar}</span>Lease Price
          ${formatNumber(leasePriceRange.low)}
          {' - '}
          {rangeHighToString(leasePriceRange.high, LEASE_PRICE_RANGE_MAX, false, formatNumber(leasePriceRange.high))}
        </div>
        <div className="range-slider">
          <Range
            className="range-slider"
            min={LEASE_PRICE_RANGE_MIN}
            max={LEASE_PRICE_RANGE_ULTD}
            step={LEASE_PRICE_RANGE_STEP}
            defaultValue={[leasePriceRange.low, leasePriceRange.high]}
            onChange={onChangeLease}
            onAfterChange={onAfterChangeLease}
            allowCross={false}
            pushable={LEASE_PRICE_RANGE_STEP}
          />
        </div>
      </div>
      : <div />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showLeasePrice, leasePriceRange.high, leasePriceRange.low]);

  const label = useMemo(() => {
    setListPriceRange(props.listPriceRange);
    setLeasePriceRange(props.leasePriceRange);
    if (props.showListPrice && props.listPriceRange.low === PRICE_RANGE_MIN && props.listPriceRange.high > PRICE_RANGE_MAX) {
      return 'Price';
    } else if (!props.showListPrice && props.showLeasePrice && props.leasePriceRange.low === LEASE_PRICE_RANGE_MIN && props.leasePriceRange.high > LEASE_PRICE_RANGE_MAX) {
      return 'Price';
    } else {
      return (
        <div>
          {props.showListPrice &&
            <div>{'$' + convertToInternationalCurrencySystem(props.listPriceRange.low) + ' - ' + rangeHighToString(props.listPriceRange.high, PRICE_RANGE_MAX, true, convertToInternationalCurrencySystem(props.listPriceRange.high))}</div>
          }
          {props.showLeasePrice &&
            <div>{'$' + props.leasePriceRange.low + ' - ' + rangeHighToString(props.leasePriceRange.high, LEASE_PRICE_RANGE_MAX, true, props.leasePriceRange.high.toString())}</div>
          }
        </div>
      );
    }
  }, [props.listPriceRange, props.leasePriceRange, props.showListPrice, props.showLeasePrice]);

  if (!props.isFloating) {
    return (
      <div className="price-range-column">
        {listPriceControl}
        {leasePriceControl}
      </div>
    )
  }

  return (
    <div>
      <button className={props.isOpen ? 'filter-button filter-button-selected' : (props.listPriceRange.low !== 0 || props.listPriceRange.high !== PRICE_RANGE_ULTD) ? 'filter-button filter-button-enabled' : 'filter-button'} onClick={props.open}>{label}</button>
      {props.isOpen &&
        <SearchFilterContainer close={props.close} alignRight>
          <div className="price-range-column">
            {listPriceControl}
            {leasePriceControl}
          </div>
        </SearchFilterContainer>
      }
    </div>
  );
}
